import React, { useState, useEffect, useCallback } from "react";
import UploadCourseForm from "../../Components/UploadCourseForm";
import axiosInstance from "../../services/axiosinstance";
import { apiUrl } from "../../routes/apiRoutes";
import {
  Plus,
  BookOpen,
  Loader2,
  Lock,
  Unlock,
  IndianRupee,
  Pencil,
  Trash2,
  Eye,
  X,
  CircleArrowLeft,
} from "lucide-react";

const CourseManagementPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [detailsCourse, setDetailsCourse] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const controller = new AbortController();

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(apiUrl.GETCOURSES, {
        signal: controller.signal,
      });
      if (res.data?.status && Array.isArray(res.data?.data)) {
        setCourses(res.data.data);
      } else {
        setCourses([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load courses");
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCourses();
    return () => {
      controller.abort();
    };
  }, [fetchCourses]);

  const handleSave = () => {
    setShowForm(false);
    setEditingCourse(null);
    fetchCourses();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCourse(null);
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setShowForm(true);
  };

  const handleDelete = async (course) => {
    if (
      !window.confirm(`Delete "${course.courseName}"? This cannot be undone.`)
    )
      return;
    setDeletingId(course._id);
    try {
      await axiosInstance.delete(`${apiUrl.DELETECOURSE}/${course._id}`);
      await fetchCourses();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to delete course";
      alert(msg);
    } finally {
      setDeletingId(null);
    }
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <div
          onClick={() => {setShowForm(false)}}
          className="flex text-red-500/50 hover:text-red-500 underline-offset-4 mb-3 underline items-center  px-2 py-1 rounded-md w-fit gap-2"
        >
          <CircleArrowLeft size={24} />
          <span>back</span>
        </div>

        <UploadCourseForm
          initialData={editingCourse ?? undefined}
          isEditing={!!editingCourse}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-200">Courses</h2>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700"
        >
          <Plus size={16} />
          Add new course
        </button>
      </div>

      <section>
        <h3 className="mb-3 text-xs font-medium  first-letter:uppercase tracking-wide text-slate-500">
          Uploaded courses
        </h3>
        {loading ? (
          <div className="flex items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12">
            <Loader2 size={28} className="animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-4 text-center text-sm text-red-400">
            {error}
          </div>
        ) : courses.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12 text-center">
            <BookOpen size={36} className="text-slate-300" />
            <p className="mt-2 text-sm text-slate-500">No courses yet</p>
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="mt-2 text-sm font-medium text-slate-200 underline hover:no-underline"
            >
              Add your first course
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {courses.map((course) => (
              <article
                key={course._id}
                className="flex flex-col overflow-hidden rounded-lg border border-[#1d3350] bg-[#0f1c2e] shadow-sm transition-shadow hover:shadow"
              >
                <div className="h-24 w-full shrink-0 bg-[#13233a]">
                  {course.poster ? (
                    <img
                      src={course.poster}
                      alt={course.courseName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <BookOpen size={28} className="text-slate-300" />
                    </div>
                  )}
                </div>
                <div className="flex min-h-0 flex-1 flex-col p-2.5">
                  <h4 className="truncate text-sm font-semibold text-slate-200">
                    {course.courseName}
                  </h4>
                  <p className="mt-0.5 truncate text-xs  first-letter:uppercase text-slate-500">
                    {course.category} | {course.level} |{" "}
                    <span
                      className={`${course?.mode === "online" ? "bg-green-400" : "bg-blue-400"} rounded-full px-2 text-white`}
                    >
                      {course.mode}
                    </span>{" "}
                    |{" "}
                    <span className="bg-yellow-500/20 text-yellow-300 px-2 rounded-full">
                      {course?.programType}
                    </span>
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {course.isPaid ? (
                      <span className="inline-flex items-center gap-0.5 rounded bg-green-500/15 px-1.5 py-0.5 text-[10px] font-medium text-green-400">
                        <IndianRupee size={10} />
                        {course.price}
                      </span>
                    ) : (
                      <span className="rounded bg-[#13233a] px-1.5 py-0.5 text-[10px] font-medium text-slate-400">
                        Free
                      </span>
                    )}
                    {course.isLocked ? (
                      <Lock
                        size={10}
                        className="text-amber-400"
                        title="Locked"
                      />
                    ) : (
                      <Unlock
                        size={10}
                        className="text-green-400"
                        title="Open"
                      />
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-1 border-t border-[#17283f] pt-2">
                    <button
                      type="button"
                      onClick={() => setDetailsCourse(course)}
                      className="flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-[#13233a] hover:text-slate-300"
                      title="View details"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEdit(course)}
                      className="flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-blue-500/10 hover:text-blue-400"
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(course)}
                      disabled={deletingId === course._id}
                      className="flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
                      title="Delete"
                    >
                      {deletingId === course._id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {detailsCourse && (
        <DetailsModal
          course={detailsCourse}
          onClose={() => setDetailsCourse(null)}
        />
      )}
    </div>
  );
};

function DetailsModal({ course, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50"
      />
      <div className="relative max-h-[85vh] w-full max-w-lg overflow-hidden rounded-xl bg-[#0f1c2e] shadow-xl">
        <div className="flex items-center justify-between border-b border-[#1d3350] px-4 py-3">
          <h3 className="font-semibold text-slate-200">Course details</h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-[#13233a]"
          >
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto p-4">
          {course.poster && (
            <div className="mb-4 aspect-video w-full overflow-hidden rounded-lg bg-[#13233a]">
              <img
                src={course.poster}
                alt={course.courseName}
                className="h-full w-full object-cover"
              />
            </div>
          )}
          <dl className="space-y-2 text-sm">
            <DetailRow label="Name" value={course.courseName} />
            <DetailRow label="Category" value={course.category} />
            <DetailRow label="Level" value={course.level} />
            <DetailRow label="Mode" value={course.mode} />
            <DetailRow label="Instructor" value={course.instructor} />
            <DetailRow
              label="Duration"
              value={course.duration ? `${course.duration} hrs` : "—"}
            />
            <DetailRow
              label="Price"
              value={course.isPaid ? `₹${course.price}` : "Free"}
            />
            <DetailRow
              label="Access"
              value={course.isLocked ? "Locked" : "Open"}
            />
            {course.tags?.length > 0 && (
              <DetailRow label="Tags" value={course.tags.join(", ")} />
            )}
            {course.description && (
              <div>
                <dt className="font-medium text-slate-500">Description</dt>
                <dd className="mt-0.5 text-slate-300">{course.description}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <dt className="font-medium text-slate-500">{label}</dt>
      <dd className="mt-0.5  first-letter:uppercase text-slate-200">
        {value ?? "—"}
      </dd>
    </div>
  );
}

export default CourseManagementPage;